import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { LocationApi, LocationsFilter } from '@/pages/Business/pages/Shippings/pages/Locations/models/Location';
import { API_BUSINESS_URL } from '@/utils';

export type GetLocationsQueryArgs = {
  filters: LocationsFilter;
  paginated: PaginatedRequest;
};

const locationsService = createApi({
  reducerPath: 'businessLocationsService',
  baseQuery: createBaseQueryWithReauth(API_BUSINESS_URL),
  tagTypes: ['Locations'],
  endpoints: (builder) => ({
    getLocations: builder.query<PaginatedResponse<LocationApi>, GetLocationsQueryArgs>({
      query: ({ paginated, filters }: GetLocationsQueryArgs) => {
        const { name, logisticOperatorId } = filters;

        const params: Record<string, string | number> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (name && name.trim() !== '') {
          params.name = name.trim();
        }
        if (logisticOperatorId && logisticOperatorId.trim() !== '') {
          params.logisticOperatorId = logisticOperatorId.trim();
        }

        return {
          url: '/locations',
          params,
        };
      },
      providesTags: ['Locations'],
    }),

    getLocationById: builder.query<LocationApi, { id: string }>({
      query: ({ id }) => ({
        url: `/location/${id}`,
      }),
      providesTags: ['Locations'],
    }),

    createLocation: builder.mutation<LocationApi, Partial<LocationApi>>({
      query: (body) => ({
        url: '/location',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Locations'],
    }),

    updateLocation: builder.mutation<LocationApi, { id: string; body: Partial<LocationApi> }>({
      query: ({ id, body }) => ({
        url: `/location/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Locations'],
    }),

    deleteLocation: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/location/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Locations'],
    }),
  }),
});

export default locationsService;

export const {
  useGetLocationsQuery,
  useGetLocationByIdQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
} = locationsService;
